import React from 'react';
import { Phone, Ruler, Scale, AlertTriangle, FileText, Gauge } from 'lucide-react';
export function VehicleLoadSizePage() {
  return <div className="min-h-screen bg-gray-50" data-id="element-3762">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-3763">
        <div className="container mx-auto px-4" data-id="element-3764">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center" data-id="element-3765">
            Michigan Weight & Axle Load Limits
          </h1>
          <p className="text-xl text-gray-100 text-center" data-id="element-3766">
            Maximum Legal Truck Loadings and Dimensions
          </p>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-yellow-50 border-b-2 border-yellow-500" data-id="element-3767">
        <div className="container mx-auto px-4 py-4" data-id="element-3768">
          <div className="max-w-4xl mx-auto" data-id="element-3769">
            <div className="flex items-start" data-id="element-3770">
              <AlertTriangle className="text-yellow-600 mr-3 flex-shrink-0 mt-1" size={24} data-id="element-3771" />
              <div data-id="element-3772">
                <p className="text-sm text-gray-800 mb-2" data-id="element-3773">
                  <strong data-id="element-3774">Important:</strong> Due to frequent changes in federal
                  and state regulations, the Michigan Center for Truck Safety
                  cannot ensure the accuracy of the material contained beyond
                  the date of publication.
                </p>
                <p className="text-sm text-gray-800" data-id="element-3775">
                  For current information, contact the Center at{' '}
                  <a href="tel:8006824682" className="text-blue-600 hover:underline font-semibold" data-id="element-3776">
                    (800) 682-4682
                  </a>
                  . This document is not intended for legal purposes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-3777">
        <div className="max-w-6xl mx-auto" data-id="element-3778">
          {/* Seasonal Restrictions Note */}
          <div className="bg-red-50 border-2 border-red-500 rounded-lg shadow-md p-6 mb-8" data-id="element-3779">
            <h2 className="text-xl font-bold text-red-900 mb-4" data-id="element-3780">
              SEASONAL LOAD RESTRICTIONS
            </h2>
            <div className="space-y-3 text-gray-800" data-id="element-3781">
              <p className="font-semibold" data-id="element-3782">
                When restricted loadings are in effect:
              </p>
              <div className="grid md:grid-cols-2 gap-4" data-id="element-3783">
                <div className="bg-white rounded-lg p-4" data-id="element-3784">
                  <p className="font-bold text-red-900 mb-2" data-id="element-3785">
                    Rigid Pavements:
                  </p>
                  <ul className="space-y-1 text-sm" data-id="element-3786">
                    <li data-id="element-3787">
                      • Normal maximum axle weights reduced <strong data-id="element-3788">25%</strong>
                    </li>
                    <li data-id="element-3789">
                      • Maximum wheel load: <strong data-id="element-3790">525 lbs per inch</strong> of
                      tire width
                    </li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-4" data-id="element-3791">
                  <p className="font-bold text-red-900 mb-2" data-id="element-3792">
                    Flexible Pavements:
                  </p>
                  <ul className="space-y-1 text-sm" data-id="element-3793">
                    <li data-id="element-3794">
                      • Normal maximum axle weights reduced <strong data-id="element-3795">35%</strong>
                    </li>
                    <li data-id="element-3796">
                      • Maximum wheel load: <strong data-id="element-3797">450 lbs per inch</strong> of
                      tire width
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Maximum Overall Dimensions */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-3798">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-3799">
              <Ruler className="mr-3 text-[#B1050B]" size={28} data-id="element-3800" />
              Maximum Overall Dimensions
            </h2>

            <div className="space-y-4" data-id="element-3801">
              <div className="grid md:grid-cols-2 gap-4" data-id="element-3802">
                <div className="bg-blue-50 rounded-lg p-4" data-id="element-3803">
                  <p className="font-bold text-gray-900 mb-2" data-id="element-3804">Width</p>
                  <p className="text-2xl font-bold text-blue-600" data-id="element-3805">96 inches</p>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-3806">
                    102 inches on designated highways
                  </p>
                </div>

                <div className="bg-green-50 rounded-lg p-4" data-id="element-3807">
                  <p className="font-bold text-gray-900 mb-2" data-id="element-3808">Height</p>
                  <p className="text-2xl font-bold text-green-600" data-id="element-3809">
                    13 feet, 6 inches
                  </p>
                </div>

                <div className="bg-yellow-50 rounded-lg p-4" data-id="element-3810">
                  <p className="font-bold text-gray-900 mb-2" data-id="element-3811">
                    Semitrailer Length (with load)
                  </p>
                  <p className="text-2xl font-bold text-yellow-600" data-id="element-3812">53 feet</p>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-3813">
                    50 feet on non-designated highways
                  </p>
                </div>

                <div className="bg-purple-50 rounded-lg p-4" data-id="element-3814">
                  <p className="font-bold text-gray-900 mb-2" data-id="element-3815">
                    Other Vehicle Length
                  </p>
                  <p className="text-2xl font-bold text-purple-600" data-id="element-3816">40 feet</p>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-3817">
                    Excluding impact absorbing bumpers
                  </p>
                </div>

                <div className="bg-red-50 rounded-lg p-4" data-id="element-3818">
                  <p className="font-bold text-gray-900 mb-2" data-id="element-3819">
                    Combination Length
                  </p>
                  <p className="text-2xl font-bold text-red-600" data-id="element-3820">65 feet</p>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-3821">
                    Truck-tractor + semitrailer + trailer
                  </p>
                </div>

                <div className="bg-indigo-50 rounded-lg p-4" data-id="element-3822">
                  <p className="font-bold text-gray-900 mb-2" data-id="element-3823">
                    Axle Limitation
                  </p>
                  <p className="text-2xl font-bold text-indigo-600" data-id="element-3824">
                    11 axles max
                  </p>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-3825">
                    Combination of vehicles
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-6 mt-6" data-id="element-3826">
                <h3 className="font-bold text-gray-900 mb-4" data-id="element-3827">
                  Additional Requirements for Semitrailers Longer than 50 feet:
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-3828">
                  <li className="flex items-start" data-id="element-3829">
                    <span className="mr-2" data-id="element-3830">•</span>
                    <span data-id="element-3831">
                      Wheelbase of <strong data-id="element-3832">37 to 41 feet</strong> (kingpin to
                      center of axles/tandem assembly)
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-3833">
                    <span className="mr-2" data-id="element-3834">•</span>
                    <span data-id="element-3835">
                      Limited to <strong data-id="element-3836">3 axles</strong>
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-3837">
                    <span className="mr-2" data-id="element-3838">•</span>
                    <span data-id="element-3839">
                      Operate on <strong data-id="element-3840">designated highways only</strong>
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-blue-50 rounded-lg p-6" data-id="element-3841">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-3842">
                  Projections & Overhang:
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-3843">
                  <li data-id="element-3844">
                    • <strong data-id="element-3845">Front projection:</strong> 3 feet maximum
                  </li>
                  <li data-id="element-3846">
                    • <strong data-id="element-3847">Rear overhang:</strong> Any amount if legal length
                    not exceeded
                  </li>
                  <li data-id="element-3848">
                    • If rear overhang is <strong data-id="element-3849">4 feet or more:</strong>{' '}
                    12-inch red square flag (daytime) and red light/lantern
                    (night)
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Weight Limits Table */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-3850">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-3851">
              <Scale className="mr-3 text-[#B1050B]" size={28} data-id="element-3852" />
              Maximum Allowable Axle Loadings
            </h2>

            <div className="overflow-x-auto" data-id="element-3853">
              <table className="w-full border-collapse text-sm" data-id="element-3854">
                <thead data-id="element-3855">
                  <tr className="bg-gray-200" data-id="element-3856">
                    <th className="border border-gray-300 px-3 py-2 text-left" data-id="element-3857">
                      Spacing Between Axles
                    </th>
                    <th className="border border-gray-300 px-3 py-2 text-center" data-id="element-3858">
                      Normal Loadings
                      <br data-id="element-3859" />
                      (55 MPH)
                      <br data-id="element-3860" />
                      Vehicles &gt;80,000 lbs
                    </th>
                    <th className="border border-gray-300 px-3 py-2 text-center" data-id="element-3861">
                      Normal Loadings
                      <br data-id="element-3862" />
                      (55 MPH)
                      <br data-id="element-3863" />
                      Vehicles ≤80,000 lbs
                    </th>
                    <th className="border border-gray-300 px-3 py-2 text-center" data-id="element-3864">
                      Seasonal (35 MPH)
                      <br data-id="element-3865" />
                      Rigid
                    </th>
                    <th className="border border-gray-300 px-3 py-2 text-center" data-id="element-3866">
                      Seasonal (35 MPH)
                      <br data-id="element-3867" />
                      Flexible
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-3868">
                  <tr className="bg-white" data-id="element-3869">
                    <td className="border border-gray-300 px-3 py-2 font-semibold" data-id="element-3870">
                      9 feet or over
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-3871">
                      18,000 lbs
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-3872">
                      20,000 lbs
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-3873">
                      13,500 lbs
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-3874">
                      11,700 lbs
                    </td>
                  </tr>
                  <tr className="bg-gray-50" data-id="element-3875">
                    <td className="border border-gray-300 px-3 py-2 font-semibold" data-id="element-3876">
                      More than 3½ ft but less than 9 ft
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-3877">
                      13,000 lbs
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-3878">
                      13,000 lbs
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-3879">
                      9,750 lbs
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-3880">
                      8,450 lbs
                    </td>
                  </tr>
                  <tr className="bg-white" data-id="element-3881">
                    <td className="border border-gray-300 px-3 py-2 font-semibold" data-id="element-3882">
                      Tandem axle assembly
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-3883">
                      *16,000 lbs
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-3884">
                      34,000 lbs on tandem
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-3885">
                      **12,000 lbs
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-3886">
                      ***10,400 lbs
                    </td>
                  </tr>
                  <tr className="bg-gray-50" data-id="element-3887">
                    <td className="border border-gray-300 px-3 py-2 font-semibold" data-id="element-3888">
                      Less than 3½ feet
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-3889">
                      9,000 lbs
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-3890">
                      9,000 lbs
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-3891">
                      6,750 lbs
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-3892">
                      5,850 lbs
                    </td>
                  </tr>
                  <tr className="bg-blue-50" data-id="element-3893">
                    <td className="border border-gray-300 px-3 py-2 font-semibold" data-id="element-3894">
                      Max wheel load (lbs/inch tire width)
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-3895">
                      700 lbs
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-3896">
                      700 lbs
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-3897">
                      525 lbs
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center" data-id="element-3898">
                      450 lbs
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-6 space-y-3 text-sm text-gray-700" data-id="element-3899">
              <p data-id="element-3900">
                <strong data-id="element-3901">*</strong> On any legal combination exceeding 80,000
                lbs, only ONE tandem axle assembly permitted at 16,000 lbs per
                axle; others limited to 13,000 lbs per axle. When ≤80,000 lbs,
                TWO tandem assemblies permitted at 16,000 lbs per axle.
              </p>
              <p data-id="element-3902">
                <strong data-id="element-3903">**</strong> On any legal combination exceeding 80,000
                lbs, only ONE tandem axle assembly permitted at 12,000 lbs per
                axle; others limited to 9,750 lbs per axle. When ≤80,000 lbs,
                TWO tandem assemblies permitted at 12,000 lbs per axle.
              </p>
              <p data-id="element-3904">
                <strong data-id="element-3905">***</strong> On any legal combination exceeding 80,000
                lbs, only ONE tandem axle assembly permitted at 10,400 lbs per
                axle; others limited to 8,450 lbs per axle. When ≤80,000 lbs,
                TWO tandem assemblies permitted at 10,400 lbs per axle.
              </p>
            </div>
          </div>

          {/* Gross Vehicle Weight Formula */}
          <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-3906">
            <h2 className="text-3xl font-bold mb-6" data-id="element-3907">
              Gross Vehicle Weight Limit
            </h2>

            <div className="bg-white/10 rounded-lg p-6 mb-6" data-id="element-3908">
              <p className="text-xl font-bold mb-4" data-id="element-3909">Maximum: 80,000 lbs</p>
              <p className="mb-4" data-id="element-3910">
                Plus the following formula for consecutive axle groups:
              </p>

              <div className="bg-white/20 rounded-lg p-4 font-mono text-lg" data-id="element-3911">
                <p className="text-center" data-id="element-3912">W = 500 × (LN/(N-1) + 12N + 36)</p>
              </div>
            </div>

            <div className="bg-white/10 rounded-lg p-6" data-id="element-3913">
              <p className="font-bold mb-3" data-id="element-3914">Where:</p>
              <ul className="space-y-2" data-id="element-3915">
                <li data-id="element-3916">
                  <strong data-id="element-3917">W</strong> = Overall gross weight on group of 2+
                  consecutive axles (nearest 500 lbs)
                </li>
                <li data-id="element-3918">
                  <strong data-id="element-3919">L</strong> = Distance in feet between extremes of axle
                  group
                </li>
                <li data-id="element-3920">
                  <strong data-id="element-3921">N</strong> = Number of axles in the group
                </li>
              </ul>
            </div>

            <div className="bg-yellow-50 text-gray-900 rounded-lg p-6 mt-6" data-id="element-3922">
              <p className="font-bold mb-2" data-id="element-3923">Exception:</p>
              <p className="text-sm" data-id="element-3924">
                2 consecutive sets of tandem axles may carry{' '}
                <strong data-id="element-3925">34,000 lbs each</strong> if first and last axles are{' '}
                <strong data-id="element-3926">not less than 36 feet apart</strong>, and gross vehicle
                weight does not exceed 80,000 lbs including all enforcement
                tolerances.
              </p>
            </div>
          </div>

          {/* Exceptions */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-3927">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-3928">
              Exceptions
            </h2>

            <div className="space-y-6" data-id="element-3929">
              <div className="bg-blue-50 rounded-lg p-6" data-id="element-3930">
                <h3 className="font-bold text-blue-900 mb-3" data-id="element-3931">
                  WIDTH EXCEPTIONS:
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-3932">
                  <li data-id="element-3933">
                    •{' '}
                    <strong data-id="element-3934">
                      Unprocessed Logs, Pulpwood, Wood Bolts, Agricultural
                      Products, Concrete Pipe:
                    </strong>{' '}
                    108 inches (load only)
                  </li>
                  <li data-id="element-3935">
                    • <strong data-id="element-3936">Buses:</strong> 102 inches
                  </li>
                  <li data-id="element-3937">
                    • <strong data-id="element-3938">Implements of Husbandry:</strong> Any width for
                    normal farming operations
                  </li>
                </ul>
              </div>

              <div className="bg-green-50 rounded-lg p-6" data-id="element-3939">
                <h3 className="font-bold text-green-900 mb-3" data-id="element-3940">
                  LENGTH EXCEPTIONS:
                </h3>

                <div className="space-y-4" data-id="element-3941">
                  <div data-id="element-3942">
                    <p className="font-semibold text-gray-900 mb-2" data-id="element-3943">
                      Truck-Tractor + Semitrailer + Trailer (or 2 Semitrailers):
                    </p>
                    <p className="text-gray-700" data-id="element-3944">
                      No overall length limit if each unit ≤28 ft 6 in OR
                      combined length ≤58 ft (measured front of first to rear of
                      second unit)
                    </p>
                  </div>

                  <div data-id="element-3945">
                    <p className="font-semibold text-gray-900 mb-2" data-id="element-3946">
                      Truck + Trailer/Semitrailer:
                    </p>
                    <p className="text-gray-700" data-id="element-3947">
                      Overall length ≤65 feet (designated highways only)
                    </p>
                  </div>

                  <div data-id="element-3948">
                    <p className="font-semibold text-gray-900 mb-2" data-id="element-3949">
                      Logging Vehicles (saw logs, pulpwood, tree length poles):
                    </p>
                    <p className="text-gray-700" data-id="element-3950">
                      Overall length ≤70 feet including load (designated
                      highways only)
                    </p>
                  </div>

                  <div data-id="element-3951">
                    <p className="font-semibold text-gray-900 mb-2" data-id="element-3952">
                      Auto/RV/Boat Transporters:
                    </p>
                    <p className="text-gray-700" data-id="element-3953">
                      ≤65 feet (stinger-steered ≤75 feet). Load may extend 3 ft
                      front, 4 ft rear
                    </p>
                  </div>

                  <div data-id="element-3954">
                    <p className="font-semibold text-gray-900 mb-2" data-id="element-3955">
                      Articulated Buses (Public Transit):
                    </p>
                    <p className="text-gray-700" data-id="element-3956">Maximum 65 feet</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Special Vehicles */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-3957">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-3958">
              Special Vehicle Requirements
            </h2>

            <div className="space-y-4" data-id="element-3959">
              <div className="bg-yellow-50 rounded-lg p-6" data-id="element-3960">
                <h3 className="font-bold text-yellow-900 mb-3" data-id="element-3961">
                  MOBILE HOMES:
                </h3>
                <ul className="space-y-1 text-gray-700" data-id="element-3962">
                  <li data-id="element-3963">
                    • Body length: <strong data-id="element-3964">45 feet</strong>
                  </li>
                  <li data-id="element-3965">
                    • With towing unit: <strong data-id="element-3966">60 feet</strong>
                  </li>
                  <li data-id="element-3967">
                    • Width: <strong data-id="element-3968">8 feet 4 inches</strong>
                  </li>
                  <li data-id="element-3969">
                    • Height: <strong data-id="element-3970">12 feet 6 inches</strong>
                  </li>
                </ul>
              </div>

              <div className="bg-red-50 rounded-lg p-6" data-id="element-3971">
                <h3 className="font-bold text-red-900 mb-3" data-id="element-3972">
                  FLAMMABLE LIQUID TRANSPORT (Flash point ≤70°F):
                </h3>
                <ul className="space-y-1 text-gray-700" data-id="element-3973">
                  <li data-id="element-3974">
                    • Must meet Department of State Police safety standards
                  </li>
                  <li data-id="element-3975">
                    • Vehicles manufactured after July 27, 1978:{' '}
                    <strong data-id="element-3976">11 feet 8½ inches maximum height</strong>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Movement Restrictions */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-3977">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-3978">
              <Gauge className="mr-3 text-[#B1050B]" size={28} data-id="element-3979" />
              Movement Restrictions
            </h2>

            <div className="space-y-4" data-id="element-3980">
              <div className="bg-red-50 border-l-4 border-red-500 p-6" data-id="element-3981">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-3982">
                  Counties with 600,000+ Population:
                </h3>

                <div className="space-y-3" data-id="element-3983">
                  <div data-id="element-3984">
                    <p className="font-semibold text-gray-900 mb-2" data-id="element-3985">
                      Truck/Truck-Tractor + Semitrailer (more than 9,000
                      gallons):
                    </p>
                    <p className="text-gray-700" data-id="element-3986">
                      Movement allowed ONLY between{' '}
                      <strong data-id="element-3987">midnight to 6:00 a.m.</strong> on designated
                      routes
                    </p>
                  </div>

                  <div data-id="element-3988">
                    <p className="font-semibold text-gray-900 mb-2" data-id="element-3989">
                      Truck-Tractor + Semitrailer + Trailer (or 2 Semitrailers):
                    </p>
                    <p className="text-gray-700" data-id="element-3990">
                      Movement allowed ONLY between{' '}
                      <strong data-id="element-3991">midnight to 6:00 a.m.</strong> on designated
                      routes
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Maximum Truck Speed */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-3992">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-3993">
              Maximum Truck Speed
            </h2>

            <div className="bg-blue-50 rounded-lg p-6" data-id="element-3994">
              <p className="text-gray-700 mb-4" data-id="element-3995">
                No truck, tractor or tractor with trailer, or any combination
                with gross weight (loaded or unloaded){' '}
                <strong data-id="element-3996">exceeding 10,000 lbs</strong> shall exceed:
              </p>
              <ul className="space-y-2 text-gray-700" data-id="element-3997">
                <li data-id="element-3998">
                  • The posted speed limit OR the speed limit on permit
                  (whichever is less)
                </li>
                <li data-id="element-3999">
                  • <strong data-id="element-4000">35 MPH</strong> where reduced loadings are enforced
                  during seasonal weight restrictions
                </li>
              </ul>
            </div>
          </div>

          {/* Special Permits */}
          <div className="bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-4001">
            <h2 className="text-3xl font-bold mb-6" data-id="element-4002">
              Special Permit Information
            </h2>

            <div className="space-y-4" data-id="element-4003">
              <p className="text-lg" data-id="element-4004">
                Vehicles or loads exceeding legal dimensions or weights require
                a <strong data-id="element-4005">SPECIAL TRANSPORT PERMIT</strong> for travel.
              </p>

              <div className="bg-white/10 rounded-lg p-6" data-id="element-4006">
                <h3 className="font-bold mb-3" data-id="element-4007">Permits issued only for:</h3>
                <p className="text-gray-100" data-id="element-4008">
                  Occasional movement of oversize or overweight vehicles/loads
                  that cannot be dismantled, reduced, or rearranged to come
                  within legal limits.
                </p>
              </div>

              <div className="bg-white/10 rounded-lg p-6" data-id="element-4009">
                <h3 className="font-bold mb-3" data-id="element-4010">Application Methods:</h3>
                <ul className="space-y-2 text-gray-100" data-id="element-4011">
                  <li data-id="element-4012">
                    • Michigan Permit and Routing System (MiPARs) computer
                    program
                  </li>
                  <li data-id="element-4013">
                    • In person at MDOT Transportation Service Centers (TSCs)
                  </li>
                  <li data-id="element-4014">• Through a permit service</li>
                </ul>
              </div>

              <div className="bg-red-50 text-gray-900 rounded-lg p-6" data-id="element-4015">
                <p className="font-bold" data-id="element-4016">
                  ⚠️ No overweight permits issued when weight restrictions are
                  in effect
                </p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-4017">
            <h2 className="text-3xl font-bold mb-4" data-id="element-4018">
              Need Help with Weight & Load Compliance?
            </h2>
            <p className="text-xl mb-6" data-id="element-4019">
              Expert guidance on Michigan weight limits, dimensions, and special
              permits
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-4020">
              <a href="tel:2484788382" className="inline-flex items-center justify-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-4021">
                <Phone className="mr-3" size={24} data-id="element-4022" />
                Call (248) 478-8382
              </a>
              <a href="tel:8006824682" className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-4023">
                <Phone className="mr-3" size={24} data-id="element-4024" />
                MI Truck Safety: (800) 682-4682
              </a>
            </div>
            <p className="mt-4 text-gray-300" data-id="element-4025">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}